import { useQuery, useMutation } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { GitBranch, GitPullRequest, RotateCcw, Send, Terminal } from 'lucide-react';
import { api, type RunRow } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import {
  EmptyState,
  ErrorNote,
  Panel,
  StatusBadge,
  anyMetered,
  cost,
  costTitle,
  when,
} from '../../components/kit';
import { Feedback, RunTranscript, SELECT as FILTER } from './shared';

export function Ledger({
  tenant,
  runId,
  onClearRun,
  projects,
  transcriptFor,
  feedbackFor,
  onTranscript,
  onFeedback,
  onSent,
}: {
  tenant: string | null;
  /** One run, asked for by a notification's link. */
  runId: string | null;
  onClearRun: () => void;
  projects: string[];
  transcriptFor: string | null;
  feedbackFor: string | null;
  onTranscript: (id: string | null) => void;
  onFeedback: (id: string | null) => void;
  onSent: () => void;
}) {
  // The queue only lists what is queued, so a failed task has no row of its own
  // to be put back from. This is that button, where the failure is read.
  const retry = useMutation({
    mutationFn: (taskId: string) => api.post(`/api/tasks/${taskId}/retry`),
    onSuccess: onSent,
  });
  const [outcome, setOutcome] = useState('');
  const [project, setProject] = useState('');
  const [search, setSearch] = useState('');
  const [pages, setPages] = useState<string[]>([]);

  const before = pages.at(-1);
  const query = useQuery({
    queryKey: ['ledger', tenant, runId, outcome, project, search, before],
    queryFn: () => {
      const params = new URLSearchParams();
      if (runId) params.set('run', runId);
      if (tenant) params.set('tenant', tenant);
      if (outcome) params.set('outcome', outcome);
      if (project) params.set('project', project);
      if (search.trim()) params.set('q', search.trim());
      if (before) params.set('before', before);
      return api.get<{ recent: RunRow[]; more: boolean; cursor: string | null }>(
        `/api/runs?${params.toString()}`,
      );
    },
    placeholderData: (previous) => previous,
  });

  // Any change of filter starts the paging over; keeping a cursor from a
  // different filter would page into rows that no longer match it.
  const filter = (apply: () => void) => {
    apply();
    setPages([]);
  };

  const rows = query.data?.recent ?? [];
  // Kept for an installation that pays per run, dropped for one that does not.
  const metered = anyMetered(rows);
  const columns = metered ? 7 : 6;

  return (
    <Panel
      description="Every finished run, newest first."
      actions={
        <>
          <select
            aria-label="Outcome"
            value={outcome}
            onChange={(event) => filter(() => setOutcome(event.target.value))}
            className={FILTER}
          >
            <option value="">Any outcome</option>
            <option value="ok">Done</option>
            <option value="failed">Failed</option>
          </select>
          {projects.length > 1 && (
            <select
              aria-label="Repository"
              value={project}
              onChange={(event) => filter(() => setProject(event.target.value))}
              className={FILTER}
            >
              <option value="">Any repository</option>
              {projects.map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
            </select>
          )}
          <Input
            className="h-7 w-48"
            placeholder="Search title or branch"
            value={search}
            onChange={(event) => filter(() => setSearch(event.target.value))}
          />
        </>
      }
    >
      {runId && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-[5px] border border-rule bg-raised px-3 py-2">
          <span className="text-[12.5px] text-ink-2">
            Showing the one run this link points at.
          </span>
          <Button variant="outline" size="sm" onClick={onClearRun}>
            Show every run
          </Button>
        </div>
      )}
      {query.isPending ? (
        <Skeleton className="h-20 w-full" />
      ) : rows.length === 0 ? (
        <EmptyState
          title={runId ? 'That run is not here' : outcome || project || search ? 'No run matches that' : 'No finished runs yet'}
          hint={
            runId
              ? 'It may belong to a tenant you cannot see, or it may have been a run that never finished.'
              : outcome || project || search
                ? 'Widen the filter, or clear it.'
                : undefined
          }
        />
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[8.5rem]">Outcome</TableHead>
                <TableHead>Task</TableHead>
                <TableHead className="hidden md:table-cell">Landed</TableHead>
                <TableHead className="hidden text-right sm:table-cell">Finished</TableHead>
                <TableHead className="hidden text-right md:table-cell">Turns</TableHead>
                {metered && <TableHead className="text-right">Cost</TableHead>}
                <TableHead className="w-[7rem] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((run) => (
                <Fragment key={run.id}>
                  <TableRow>
                    <TableCell>
                      <StatusBadge state={run.ok ? 'done' : 'failed'} />
                    </TableCell>
                    <TableCell className="max-w-[20rem]">
                      <span className="data font-semibold text-ink">{run.projectKey}</span>
                      <p className="truncate text-[12.5px] text-ink-2" title={run.title}>
                        {run.title}
                      </p>
                      {/* The outcome column is dropped below md, and it is the one
                          carrying why a run failed. Dropping the reason with it
                          leaves a phone with no way to read the failure at all. */}
                      {!run.ok && run.reason && (
                        <p className="truncate text-[11.5px] text-bad md:hidden" title={run.reason}>
                          {run.reason}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="hidden max-w-[17rem] md:table-cell">
                      {run.ok ? (
                        run.mrUrl ? (
                          <a
                            className="inline-flex items-center gap-1.5 text-[12.5px] text-brand hover:underline"
                            href={run.mrUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <GitPullRequest className="size-3.5" aria-hidden /> merge request
                          </a>
                        ) : (
                          <>
                            <span className="data flex items-center gap-1.5 text-ink-2" title={run.branch ?? ''}>
                              <GitBranch className="size-3.5 shrink-0 text-ink-3" aria-hidden />
                              <span className="min-w-0 truncate">{run.branch}</span>
                            </span>
                            {run.mrError && (
                              <p className="mt-0.5 truncate text-[11.5px] text-warn" title={run.mrError}>
                                no merge request: {run.mrError}
                              </p>
                            )}
                          </>
                        )
                      ) : (
                        <span className="truncate text-[12.5px] text-bad" title={run.reason ?? ''}>
                          {run.reason}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="data hidden text-right text-ink-2 sm:table-cell">
                      {when(run.endedAt)}
                    </TableCell>
                    <TableCell className="data hidden text-right text-ink-2 md:table-cell">{run.turns}</TableCell>
                    {metered && (
                      <TableCell className="data text-right text-ink-2" title={costTitle(run.billing)}>
                        {cost(run.costUsd, run.billing)}
                      </TableCell>
                    )}
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          title="Transcript"
                          aria-expanded={transcriptFor === run.id}
                          onClick={() => onTranscript(transcriptFor === run.id ? null : run.id)}
                        >
                          <Terminal className="size-3.5" aria-hidden />
                          <span className="sr-only">Transcript</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          title="Send back to the agent"
                          aria-expanded={feedbackFor === run.id}
                          onClick={() => onFeedback(feedbackFor === run.id ? null : run.id)}
                        >
                          <Send className="size-3.5" aria-hidden />
                          <span className="sr-only">Send back</span>
                        </Button>
                        {!run.ok && (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title="Run this task again, on the same branch"
                            disabled={retry.isPending}
                            onClick={() => retry.mutate(run.taskId)}
                          >
                            <RotateCcw className="size-3.5" aria-hidden />
                            <span className="sr-only">Run again</span>
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  {transcriptFor === run.id && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={columns} className="h-auto whitespace-normal">
                        <RunTranscript id={run.id} />
                      </TableCell>
                    </TableRow>
                  )}
                  {feedbackFor === run.id && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={columns} className="h-auto whitespace-normal">
                        <Feedback taskId={run.taskId} tenantId={run.organizationId} onSent={onSent} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>

          <ErrorNote error={retry.error} />

          <div className="mt-3 flex items-center justify-between gap-3">
            <span className="text-[12px] text-ink-2">
              {pages.length > 0 ? `Page ${pages.length + 1}` : `${rows.length} run${rows.length === 1 ? '' : 's'}`}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pages.length === 0}
                onClick={() => setPages(pages.slice(0, -1))}
              >
                Newer
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!query.data?.more || !query.data?.cursor}
                onClick={() => setPages([...pages, query.data!.cursor!])}
              >
                Older
              </Button>
            </div>
          </div>
        </>
      )}
    </Panel>
  );
}
